// Preview differences 1–25: helpers that make the Preview draw what the SDK renderers draw.
import { resolveMergeTags } from "./inAppMergeTags";
import { TEXT_KEYS } from "./inAppRules";

// Preview 1 (R2-1 / F2): identifying keys are never filled server-side (P-6), so Preview shows their
// fallback, or the tag itself when there is none.
export const IDENTIFYING_KEYS = ["email", "phone"];
const keepRaw = (k: string) => IDENTIFYING_KEYS.includes(k);

type AnyBlock = { id: string; type: string; content: Record<string, any> };

// Resolve every customer-visible string of every block (nested too) with the author's sample values,
// as the SDKs do with the contact's properties.
export function resolvePreviewBlocks<T extends AnyBlock>(blocks: T[], vars: Record<string, string>): T[] {
  const props: Record<string, string> = {};
  Object.keys(vars || {}).forEach((k) => { if (!keepRaw(k)) props[k] = vars[k]; });
  const r = (v: unknown) => (typeof v === "string" ? resolveMergeTags(v, props, keepRaw) : v);
  const walk = (list: T[]): T[] => (Array.isArray(list) ? list : []).map((b) => {
    const c: Record<string, any> = { ...(b.content || {}) };
    TEXT_KEYS.forEach((k) => { if (typeof c[k] === "string") c[k] = r(c[k]); });
    ["items", "options"].forEach((k) => { if (Array.isArray(c[k])) c[k] = c[k].map(r); });
    if (Array.isArray(c.slides)) c.slides = c.slides.map((s: any) => (s && typeof s === "object" ? { ...s, title: r(s.title), description: r(s.description), cta: r(s.cta) } : s));
    if (Array.isArray(c.children)) c.children = walk(c.children);
    return { ...b, content: c };
  });
  return walk(blocks);
}

// ─── Preview 2 (R2-15): countdown, as Core InAppTime / Android CountdownRules (shared vectors
// fixtures/inapp-countdown-vectors.json) and the iOS CountdownView format. ─────────────────────
const MAX_DURATION_SECONDS = 100 * 365 * 86400;

function zoneOffsetMs(zone: string, utcMs: number): number {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" })
    .formatToParts(new Date(utcMs));
  const n = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  return Date.UTC(n("year"), n("month") - 1, n("day"), n("hour"), n("minute"), n("second")) - Math.floor(utcMs / 1000) * 1000;
}

// endDate / attribute value: epoch ms, epoch seconds (|n| < 1e11), ISO-8601 with offset or Z, or
// datetime-local (yyyy-MM-ddTHH:mm[:ss]) in `zone` (the browser's zone when absent). Else null.
export function parseCountdownEnd(v: unknown, zone?: string): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? (Math.abs(v) < 1e11 ? Math.trunc(v) * 1000 : Math.trunc(v)) : null;
  if (typeof v !== "string") return null;
  const s = v.trim();
  if (!s) return null;
  if (/^-?\d+(\.\d+)?$/.test(s)) return parseCountdownEnd(Number(s), zone);
  const local = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(s);
  if (local) {
    const [y, mo, d, h, mi, se] = local.slice(1).map((x) => Number(x || 0));
    if (!zone) { const t = new Date(y, mo - 1, d, h, mi, se).getTime(); return Number.isFinite(t) ? t : null; }
    try {
      const guess = Date.UTC(y, mo - 1, d, h, mi, se);
      const first = guess - zoneOffsetMs(zone, guess);
      return guess - zoneOffsetMs(zone, first);
    } catch { return null; }
  }
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/i.test(s)) return null;
  const t = Date.parse(s);
  return Number.isFinite(t) ? t : null;
}

export function countdownEndMs(block: Record<string, any>, firstShownMs: number | null, props: Record<string, unknown> = {}, zone?: string): number | null {
  const b = block || {};
  const mode = b.mode ?? b.countdownMode;
  if (mode === "duration") {
    const secs = typeof b.durationSeconds === "number" && b.durationSeconds > 0 && b.durationSeconds <= MAX_DURATION_SECONDS ? b.durationSeconds : null;
    return secs == null || firstShownMs == null ? null : firstShownMs + secs * 1000;
  }
  if (mode === "attribute") {
    const key = typeof b.attribute === "string" ? b.attribute.trim() : "";
    return key ? parseCountdownEnd(props[key], zone) : null;
  }
  return parseCountdownEnd(b.endDate, zone);
}

const blank = (v: unknown) => typeof v !== "string" || !v.trim();
export const countdownExpiredText = (b: Record<string, any>): string =>
  !blank(b?.expiredText) ? b.expiredText : !blank(b?.label) ? b.label : "Expired";

export function formatCountdown(ms: number, format: string | undefined, showDays: boolean): string {
  const total = Math.floor(Math.max(0, ms) / 1000);
  const days = Math.floor(total / 86400), hours = Math.floor((total % 86400) / 3600), mins = Math.floor((total % 3600) / 60), secs = total % 60;
  const p = (n: number) => String(n).padStart(2, "0");
  if (format === "ms") return `${p(mins)}:${p(secs)}`;
  if (format === "hms") return `${p(Math.floor(total / 3600))}:${p(mins)}:${p(secs)}`;
  return showDays && days > 0 ? `${days}d ${p(hours)}:${p(mins)}:${p(secs)}` : `${p(hours)}:${p(mins)}:${p(secs)}`;
}
